import { uniqBy } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { LayoutModel, ModelCategory } from '@paragon/contract-library';
import {
  CachedRowCalculator,
  DataRow,
  GenericModelSchema,
  getChildrenFinder,
  ModelChildLoader,
  ProjectSchema,
} from '@paragon/layout-models';
import { getOrganizationMembersByEmail } from '@paragon/login-tools';
import { naturalSortByProperty } from '@paragon/utilities';

import { sortProjects } from '../grid/ordering';
import { getMaxPageIndex, PAGE_SIZE } from '../grid/paging';
import { RootState } from '../slices';
import { getFilteredProjects } from './filteredProjects';

const getOrder = (state: RootState) => state.grid.order;
const getOrderByColumnName = (state: RootState) => state.grid.orderByColumnName;
const getPageIndex = (state: RootState) => state.grid.pageIndex;
const getExpandedRows = (state: RootState) => state.grid.expandedRows;

const getProjectGridLoaders = createSelector([getChildrenFinder], (getChildren) => {
  const modelChildLoader = new ModelChildLoader();
  modelChildLoader.addDefault((parent: LayoutModel, childSchema: GenericModelSchema) => {
    if (parent.category === ModelCategory.ComponentDesign) {
      return [];
    }

    return naturalSortByProperty(
      uniqBy(
        getChildren(parent.guid).filter((child) => child.type === childSchema.type),
        (child) => child.guid,
      ),
      (child) => child.name,
    );
  });
  return modelChildLoader;
});

export const getSortedProjects = createSelector(
  [getFilteredProjects, getOrderByColumnName, getOrder, getOrganizationMembersByEmail],
  (filteredProjects, orderByColumnName, order, membersByEmail) =>
    sortProjects(filteredProjects, orderByColumnName, order, { membersByEmail }),
);

export const getActivePageIndex = createSelector([getSortedProjects, getPageIndex], (projects, pageIndex) =>
  Math.min(pageIndex, getMaxPageIndex(projects.length)),
);

export const getPagedProjects = createSelector([getSortedProjects, getActivePageIndex], (projects, pageIndex) =>
  projects.slice(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE),
);

const cachedRowCalculator = new CachedRowCalculator();

/** The rows the grid actually renders: the current page, expanded, with collapsed descendants filtered out. */
export const getVisiblePageRows = createSelector(
  [getPagedProjects, getProjectGridLoaders, getExpandedRows],
  (pagedProjects, loaders, expandedRows) =>
    cachedRowCalculator
      .calculateRows(DataRow.fromArray(pagedProjects, ProjectSchema), { loaders }, expandedRows)
      .filter((rowWithMetadata) => rowWithMetadata.isVisible),
);

export const getVisiblePageRowIds = createSelector([getVisiblePageRows], (rows) =>
  rows.map((rowWithMetadata) => rowWithMetadata.row.rowId),
);
